'use client'

import * as React from 'react'
import { usePathname } from 'next/navigation'
import {
  ThemeProvider as NextThemesProvider,
  type ThemeProviderProps,
} from 'next-themes'

/**
 * Las zonas que solo existen en claro: la web publica y las pantallas de acceso.
 *
 * ANTES esto lo hacian tres componentes (ForceLightTheme y LightTheme, mas un
 * efecto en la portada) llamando a setTheme("light"). Y setTheme no es local a
 * una pagina: escribe en localStorage y next-themes lo sincroniza entre
 * pestañas. Consecuencias, las dos vistas en produccion:
 *
 *  - Entrar en /login BORRABA la preferencia de quien tenia el modo oscuro. No
 *    mientras estaba ahi: para siempre.
 *  - Con una pestaña abierta en login o en la web, poner el modo oscuro en el
 *    panel duraba milisegundos. ForceLightTheme dependia de [resolvedTheme], o
 *    sea que era un vigilante: veia el cambio y lo deshacia.
 *
 * `forcedTheme` es la respuesta de next-themes a esto: pinta el tema que se le
 * diga y NO toca localStorage, asi que la preferencia del usuario sigue intacta
 * y le espera al volver al panel. Ademas viaja en el script que next-themes
 * inyecta en el head, asi que no hay parpadeo.
 *
 * Vive aqui, y no en cada pagina, porque un provider anidado de next-themes es
 * un no-op: si ya hay uno por encima, el de dentro se ignora. El de arriba es
 * el unico sitio donde esta decision se puede tomar.
 */
const RUTAS_SOLO_CLARO = [
  // El grupo (auth) entero.
  '/login', '/register', '/forgot-password', '/reset-password', '/set-password',
  '/setup-account', '/verify-email', '/invite', '/auth',
  // Web publica: portada, landings, calculadoras, glosario y legal.
  '/empleados', '/compras', '/reporting', '/mejores', '/calculadora', '/contacto',
  '/horarios', '/facturacion', '/plantillas', '/sobre-nosotros', '/inventario',
  '/nominas', '/glosario', '/legal', '/produccion', '/precios',
  // La app dentro del panel de Shopify, que es siempre claro.
  '/tienda-shopify',
]

function soloClaro(pathname: string | null): boolean {
  if (!pathname) return false
  // La portada, solo exacta: por prefijo se llevaria la aplicacion entera.
  if (pathname === '/') return true
  // El "/" del final importa: sin el, /nominas-algo pasaria por hija de /nominas.
  return RUTAS_SOLO_CLARO.some((r) => pathname === r || pathname.startsWith(`${r}/`))
}

export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  const pathname = usePathname()

  return (
    <NextThemesProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
      {...props}
      forcedTheme={soloClaro(pathname) ? 'light' : undefined}
    >
      {children}
    </NextThemesProvider>
  )
}
